import React, { useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { groupBy } from "lodash";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { ExpandMore, Search } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";

import { ButtonIcon, HelpBox, SearchBox, VariableCategoryAccordion } from "@/components/atoms";
import { useClient } from "@/hooks";
import { DiscoverResult, Variable } from "@/types";
import { searchAi } from "@/api";
import { getVariableIcon } from "@/utils";

interface VariableDiscoverProps {
  variables: Variable[];
  onSelect: (variables: Variable[]) => void;
  onDelete: (variable: Variable) => void;
  selected: Set<number>;
  active: Set<number>;
}

const VariableDiscover: React.FC<VariableDiscoverProps> = ({
  variables,
  onSelect,
  onDelete,
  selected,
  active
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const client = useClient();

  const [searchText, setSearchText] = useState<string>("");
  const [discoverResults, setDiscoverResults] = useState<DiscoverResult | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const groupedResults = useMemo(() => {
    return discoverResults && groupBy(discoverResults.top_results, 'pillar');
  }, [discoverResults]);

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(event.target.value || "");


  const handleSearch = async (searchTerm: string) => {
    const accessToken = await getAccessTokenSilently();

    if (searchTerm.length < 2) return;
    if (!accessToken) return;

    setLoading(true);
    setError(false);
    setDiscoverResults(null);
    const { data, error } = await searchAi(
      accessToken,
      searchTerm,
      client
    );

    if (data) {
      setDiscoverResults(data);
      setLoading(false);
    }

    if (error) {
      setError(true);
      setLoading(false);
    }
  };

  const handleEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch(searchText);
    }
  }

  const isSelected = (variable: Variable) => {
    return selected.has(variable.tag_id);
  };

  const isActive = (variable: Variable) => {
    return active.has(variable.tag_id);
  }

  return (
    <>
      <Box mb={1}>
        <Typography variant="caption" >
          Describe your audience using natural language and our AI will understand the context, identify the core "pillars", and suggest the most suitable variables within each pillar.
        </Typography>
      </Box>
      <Box height={500} ref={panelRef}>
        <Stack direction="row" spacing={1} pb={2}>
          <SearchBox
            value={searchText}
            onSearch={handleSearchTextChange}
            onKeyDown={handleEnter}
            fullWidth={true}
            placeholder="People who..."
            color="secondary"
          />
          <ButtonIcon
            onClick={() => handleSearch(searchText)}
            color={searchText === "" ? "info" : "secondary"}
            size="small"
            Icon={Search}
          />
        </Stack>
        {error && (
          <Grid size={12}>
            <Alert variant="filled" severity='error' sx={{ width: '100%' }}>
              It was not possible to generate results at this time.
              You may need to adjust your query and try again.
              Please contact the support team if the problem persists.
            </Alert>
          </Grid>
        )}
        {loading ? (
          <Grid size={12}>
            <LinearProgress color="secondary"/>
          </Grid>
        ) : discoverResults ? (
          <Grid container size={12} spacing={3} ref={scrollRef} pr={2} sx={{ height: 444, overflowY: "auto", scrollBehavior: 'smooth' }}>

            <Grid size={12}>
              <Alert variant="outlined" severity="info">
                This feature is currently in beta testing and currently only includes Interest and Spend variables. Please flag and issues you encounter to our support team.
              </Alert>
            </Grid>


            <Grid size={12}>

              {/* Direct Matches */}
              {discoverResults.direct_matches.length > 0 && (
                <Accordion
                  variant="outlined"
                  defaultExpanded
                  sx={{ backgroundColor: "transparent" }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="body2">Direct Matches</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Here are some variables that I can see you have referenced directly within your Audience definition.
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    {discoverResults.direct_matches.map((variable, variableIdx) => (
                      <HelpBox
                        title={
                          isActive(variable) ? "Selected in the active query group"
                            : isSelected(variable) ? "Selected in another query group"
                              : `${variable.tag_type} | ${variable.tag_category} | ${variable.tag_name}`
                        }
                      >
                        <Chip
                          key={variable.tag_id}
                          size="small"
                          color={isActive(variable) ? "secondary" : isSelected(variable) ? "info" : "default"}
                          label={variable.tag_name}
                          icon={getVariableIcon(variable)}
                          onClick={() => !isActive(variable) && onSelect([variable])}
                          onDelete={isActive(variable) ? () => onDelete(variable) : undefined}
                          sx={{ m: 0.5 }}
                        />
                      </HelpBox>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Top Results */}
              <Accordion
                variant="outlined"
                defaultExpanded
                sx={{ backgroundColor: "transparent" }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  // sx={{
                  //   '& .MuiAccordionSummary-content': {
                  //     margin: '0 !important', // Forces the margin to stay dead flat
                  //   },
                  // }}
                >
                  <Stack spacing={0.5}>
                    <Typography variant="body2">Core Pillars</Typography>
                    <Typography variant="body2" color="textSecondary">
                      I have identified the following pillars that represent the core themes identified within your audience definition.
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>

                  {groupedResults && Object.keys(groupedResults).map((pillar, pillarIdx) => {
                    const pillarData = groupedResults[pillar];
                    const pillarInfo = discoverResults.pillars.find(x => x.name === pillar)
                    return (
                      <VariableCategoryAccordion
                        category={pillar}
                        categoryText={pillarInfo?.relevance}
                        description={pillarInfo?.description}
                        variables={pillarData}
                        score="score"
                        onSelect={onSelect}
                        onDelete={onDelete}
                        selected={selected}
                        active={active}

                      />
                    )
                  })}
                </AccordionDetails>
              </Accordion>

              {/* Correlated Variables */}
              {/*<Accordion*/}
              {/*  variant="outlined"*/}
              {/*  defaultExpanded*/}
              {/*  sx={{ backgroundColor: "transparent" }}*/}
              {/*>*/}
              {/*  <AccordionSummary*/}
              {/*    expandIcon={<ExpandMore />}*/}
              {/*    sx={{*/}
              {/*      '& .MuiAccordionSummary-content': {*/}
              {/*        margin: '0 !important', // Forces the margin to stay dead flat*/}
              {/*      },*/}
              {/*    }}*/}
              {/*  >*/}
              {/*    <Stack spacing={0.5}>*/}
              {/*      <Typography variant="body2">Correlated Variables</Typography>*/}
              {/*      <Typography variant="body2" color="textSecondary">*/}
              {/*        These variables sit outside of the core pillars but are highly correlated with your Audience definition.*/}
              {/*      </Typography>*/}
              {/*    </Stack>*/}
              {/*  </AccordionSummary>*/}
              {/*  <AccordionDetails>*/}
              {/*    {discoverResults.correlated.map((variable, variableIdx) => (*/}
              {/*      <HelpBox*/}
              {/*        title={*/}
              {/*          isActive(variable) ? "Selected in the active query group"*/}
              {/*            : isSelected(variable) ? "Selected in another query group"*/}
              {/*              : ""*/}
              {/*        }*/}
              {/*      >*/}
              {/*        <Chip*/}
              {/*          key={variable.tag_id}*/}
              {/*          size="small"*/}
              {/*          color={isActive(variable) ? "secondary" : isSelected(variable) ? "info" : "default"}*/}
              {/*          label={`${variable.tag_name} [${toIndex(variable.score)}]`}*/}
              {/*          icon={getVariableIcon(variable)}*/}
              {/*          onClick={() => !isActive(variable) && onSelect([variable])}*/}
              {/*          onDelete={isActive(variable) ? () => onDelete(variable) : undefined}*/}
              {/*          sx={{ m: 0.5 }}*/}
              {/*        />*/}
              {/*      </HelpBox>*/}
              {/*    ))}*/}
              {/*  </AccordionDetails>*/}
              {/*</Accordion>*/}
            </Grid>

          </Grid>
        ): null }
      </Box>
    </>
  );
};

export default VariableDiscover;
